import apiService from './api';
import { unwrap } from '../utils/apiResponse';
import type { Numeric, NumericValue } from '@/utils/numeric';
import type {
  AreaGeometry, DerivedFlow, InstallSpec, InstallSpecFields, IrrigationSystemType,
} from '@/utils/irrigationFlow';

export type {
  AreaGeometry, DerivedFlow, InstallSpec, InstallSpecFields, IrrigationSystemType,
  CountingMethod, FlowWarning,
} from '@/utils/irrigationFlow';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface GeoJSONLineString {
  type: 'LineString';
  coordinates: [number, number][];
}

export type NodeType = 'source' | 'junction' | 'valve' | 'sensor' | 'outlet' | 'tank';
export type SourceSubtype = 'pump' | 'tank' | 'mains' | 'bore';
export type ControlMode = 'manual' | 'relay' | 'planned';
export type ElevationSource = 'manual' | 'dem' | 'gps' | 'relative';

export interface PumpCurvePoint {
  flow_lpm: number;
  head_m: number;
}

export interface IrrigationNode {
  id: number;
  farm_id: number;
  organisation_id: number;
  node_type: NodeType;
  name: string | null;
  notes: string | null;
  lat: number;
  lng: number;
  elevation_m: Numeric;
  elevation_source: ElevationSource | null;

  source_subtype: SourceSubtype | null;
  rated_flow_lpm: Numeric;
  rated_pressure_kpa: Numeric;
  pump_curve: PumpCurvePoint[] | null;
  source_tank_sensor_channel_id: number | null;

  control_mode: ControlMode | null;
  required_pressure_kpa: Numeric;
  min_operating_pressure_kpa: Numeric;
  rated_valve_flow_lpm: Numeric;

  /**
   * Hectares of `cultivar_area_id` this valve waters.
   *
   * Null is an answer, not a gap: the derivation splits whatever hectares the
   * stated shares leave over among the valves that state none, so one valve on
   * a planting takes all of it and three take a third each without anybody
   * typing a number. Only state it where the split is uneven.
   */
  served_area_ha: Numeric;

  relay_router_mac: string | null;
  relay_index: number | null;
  sensor_channel_id: number | null;

  /**
   * Where in the orchard this node sits.
   *
   * Not the same thing as the relay's drawn zone. A zone is an outline around
   * a valve's wetted area and belongs to the relay, so it exists only where
   * hardware does; the block and cultivar area are the orchard's own
   * subdivision, and carry the cultivar, tree count and spacings behind them.
   * A sensor, a tank and a junction all sit in the orchard and have no relay.
   */
  block_id: number | null;
  cultivar_area_id: number | null;

  /**
   * The equipment record this node is — a pump's hours are counted from the
   * node's relay onto that machine's meter. Absent on payloads from before the
   * column existed.
   */
  equipment_id?: number | null;

  created_at: string;
  updated_at: string;
  created_by: number | null;
}

export interface IrrigationPipe {
  id: number;
  farm_id: number;
  organisation_id: number;
  from_node_id: number;
  to_node_id: number;
  geometry: GeoJSONLineString;
  length_m: NumericValue;
  material_key: string;
  nominal_diameter_mm: NumericValue;
  internal_diameter_mm: NumericValue;
  pressure_class_kpa: number | null;
  roughness_override: Numeric;
  installed_year: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PipeMaterial {
  key: string;
  display_name: string;
  hazen_williams_c: NumericValue;
  default_pressure_class_kpa: number | null;
  notes: string | null;
}

export interface IrrigationNetwork {
  nodes: IrrigationNode[];
  pipes: IrrigationPipe[];
}

export interface RelayLiveState {
  on: boolean;
  online: boolean;
  last_seen: string | null;
}

export interface SensorLiveReading {
  value: number | null;
  reading_time: string;
  type_key: string;
}

export interface IrrigationLiveNetwork extends IrrigationNetwork {
  relay_state: Record<string, RelayLiveState>;        // key = `${mac}:${index}`
  sensor_readings: Record<number, SensorLiveReading>; // key = sensor_channel_id
}

/** One cultivar area, as the orchard-areas picker needs it. */
export interface OrchardAreaOption {
  id: number;
  block_id: number;
  name: string;
  area_hectares: NumericValue;
  tree_count: number | null;
  row_spacing_meters?: Numeric;
  /** Free text on the cultivar area, predating any of this — shown, never matched on. */
  irrigation_zone: string | null;
  cultivar_name: string | null;
}

export interface OrchardBlockOption {
  id: number;
  name: string;
  area_hectares: NumericValue;
  cultivar_areas: OrchardAreaOption[];
}

export interface ElevationLookup {
  elevation_m: NumericValue;
  source: 'dem';
  dataset: string;
}

export type NodeInput = Partial<Omit<IrrigationNode,
  'id' | 'farm_id' | 'organisation_id' | 'created_at' | 'updated_at' | 'created_by'>> & {
    node_type: NodeType;
    lat: number;
    lng: number;
  };

export type PipeInput = Omit<IrrigationPipe,
  'id' | 'farm_id' | 'organisation_id' | 'length_m' | 'created_at' | 'updated_at'>;

// ─── Service ────────────────────────────────────────────────────────────────

const BASE = '/irrigation-network';

class IrrigationNetworkService {
  async listMaterials(): Promise<PipeMaterial[]> {
    return unwrap(await apiService.get<PipeMaterial[]>(`${BASE}/materials`));
  }

  async lookupElevation(lat: number, lng: number): Promise<ElevationLookup | null> {
    return unwrap(await apiService.get<ElevationLookup | null>(
      `${BASE}/elevation?lat=${lat}&lng=${lng}`
    ));
  }

  async getNetwork(farmId: number): Promise<IrrigationNetwork> {
    return unwrap(await apiService.get<IrrigationNetwork>(`${BASE}/farms/${farmId}/network`));
  }

  async getLiveNetwork(farmId: number): Promise<IrrigationLiveNetwork> {
    return unwrap(await apiService.get<IrrigationLiveNetwork>(`${BASE}/farms/${farmId}/network/live`));
  }

  async createNode(farmId: number, body: NodeInput): Promise<IrrigationNode> {
    return unwrap(await apiService.post<IrrigationNode>(`${BASE}/farms/${farmId}/nodes`, body));
  }

  async updateNode(id: number, body: Partial<NodeInput>): Promise<IrrigationNode> {
    return unwrap(await apiService.put<IrrigationNode>(`${BASE}/nodes/${id}`, body));
  }

  async deleteNode(id: number): Promise<void> {
    unwrap(await apiService.delete<{ deleted: boolean }>(`${BASE}/nodes/${id}`));
  }

  async createPipe(farmId: number, body: PipeInput): Promise<IrrigationPipe> {
    return unwrap(await apiService.post<IrrigationPipe>(`${BASE}/farms/${farmId}/pipes`, body));
  }

  async updatePipe(id: number, body: Partial<PipeInput>): Promise<IrrigationPipe> {
    return unwrap(await apiService.put<IrrigationPipe>(`${BASE}/pipes/${id}`, body));
  }

  async deletePipe(id: number): Promise<void> {
    unwrap(await apiService.delete<{ deleted: boolean }>(`${BASE}/pipes/${id}`));
  }

  async linkRelay(nodeId: number, routerMac: string, relayIndex: number): Promise<IrrigationNode> {
    return unwrap(await apiService.post<IrrigationNode>(
      `${BASE}/nodes/${nodeId}/link-relay`, { router_mac: routerMac, relay_index: relayIndex }
    ));
  }

  async unlinkRelay(nodeId: number): Promise<IrrigationNode> {
    return unwrap(await apiService.post<IrrigationNode>(`${BASE}/nodes/${nodeId}/unlink-relay`, {}));
  }

  async linkSensor(nodeId: number, sensorChannelId: number): Promise<IrrigationNode> {
    return unwrap(await apiService.post<IrrigationNode>(
      `${BASE}/nodes/${nodeId}/link-sensor`, { sensor_channel_id: sensorChannelId }
    ));
  }

  async unlinkSensor(nodeId: number): Promise<IrrigationNode> {
    return unwrap(await apiService.post<IrrigationNode>(`${BASE}/nodes/${nodeId}/unlink-sensor`, {}));
  }

  // ── Scenarios + solver ──────────────────────────────────────────────────

  async listScenarios(farmId: number): Promise<IrrigationScenario[]> {
    return unwrap(await apiService.get<IrrigationScenario[]>(`${BASE}/farms/${farmId}/scenarios`));
  }

  async createScenario(farmId: number, body: ScenarioInput): Promise<IrrigationScenario> {
    return unwrap(await apiService.post<IrrigationScenario>(`${BASE}/farms/${farmId}/scenarios`, body));
  }

  async updateScenario(id: number, body: Partial<ScenarioInput>): Promise<IrrigationScenario> {
    return unwrap(await apiService.put<IrrigationScenario>(`${BASE}/scenarios/${id}`, body));
  }

  async deleteScenario(id: number): Promise<void> {
    unwrap(await apiService.delete<{ deleted: boolean }>(`${BASE}/scenarios/${id}`));
  }

  async solve(farmId: number, body: SolveRequest): Promise<SolveResult> {
    return unwrap(await apiService.post<SolveResult>(`${BASE}/farms/${farmId}/solve`, body));
  }

  async analyseProgram(programId: number): Promise<ProgramAnalysis> {
    return unwrap(await apiService.get<ProgramAnalysis>(`${BASE}/programs/${programId}/analysis`));
  }

  async getLiveAnalysis(farmId: number): Promise<LiveAnalysis> {
    return unwrap(await apiService.get<LiveAnalysis>(`${BASE}/farms/${farmId}/live-analysis`));
  }

  /** Blocks on this farm, each nesting the cultivar areas inside it. */
  async listOrchardAreas(farmId: number): Promise<OrchardBlockOption[]> {
    const r = unwrap(await apiService.get<{ blocks: OrchardBlockOption[] }>(
      `${BASE}/farms/${farmId}/orchard-areas`
    ));
    return r?.blocks ?? [];
  }

  async listSensorChannels(farmId: number, types?: string[]): Promise<SensorChannelOption[]> {
    const q = types && types.length > 0 ? `?types=${encodeURIComponent(types.join(','))}` : '';
    return unwrap(await apiService.get<SensorChannelOption[]>(
      `${BASE}/farms/${farmId}/sensor-channels${q}`
    ));
  }

  // ── Installed irrigation systems ────────────────────────────────────────

  async listSystemTypes(): Promise<IrrigationSystemType[]> {
    return unwrap(await apiService.get<IrrigationSystemType[]>(`${BASE}/system-types`));
  }

  /** The spec on one planting, with the server's own derivation beside it. */
  async getAreaIrrigation(cultivarAreaId: number): Promise<AreaIrrigation> {
    return unwrap(await apiService.get<AreaIrrigation>(
      `${BASE}/cultivar-areas/${cultivarAreaId}/irrigation`
    ));
  }

  async saveAreaIrrigation(
    cultivarAreaId: number, body: InstallSpecInput,
  ): Promise<AreaIrrigation> {
    return unwrap(await apiService.put<AreaIrrigation>(
      `${BASE}/cultivar-areas/${cultivarAreaId}/irrigation`, body
    ));
  }

  async deleteAreaIrrigation(cultivarAreaId: number): Promise<void> {
    unwrap(await apiService.delete<{ deleted: boolean }>(
      `${BASE}/cultivar-areas/${cultivarAreaId}/irrigation`
    ));
  }

  async getValveDemand(farmId: number): Promise<ValveDemand> {
    return unwrap(await apiService.get<ValveDemand>(`${BASE}/farms/${farmId}/valve-demand`));
  }

  /**
   * Write a valve's derived flow onto it.
   *
   * Its own call rather than a side effect of saving a spec: the figure already
   * there may have been measured at the valve with a flow meter, which beats
   * anything this arithmetic offers.
   */
  async applyDerivedFlow(nodeId: number): Promise<IrrigationNode> {
    return unwrap(await apiService.post<IrrigationNode>(
      `${BASE}/nodes/${nodeId}/apply-derived-flow`, {}
    ));
  }
}

/** The cultivar-area columns the install panel reads back after a save. */
export interface AreaIrrigationArea extends AreaGeometry {
  id: number;
  farm_id: number;
  block_id: number;
  name: string;
  active: boolean | null;
  tree_spacing_meters: Numeric;
}

export interface AreaIrrigation {
  cultivar_area: AreaIrrigationArea;
  spec: InstallSpec | null;
  system_type: IrrigationSystemType | null;
  derived: DerivedFlow | null;
}

export type InstallSpecInput = Partial<Omit<InstallSpecFields, 'system_type_key'>>
  & { system_type_key: string };

/** Why a valve has no derived flow — every valve is listed, reason included. */
export type ValveDemandReason = 'ok' | 'no_area' | 'no_spec' | 'incomplete_spec' | 'zero_share';

export type ServedAreaBasis = 'stated' | 'even_split' | 'whole_area';

export interface ValveDemandRow {
  node_id: number;
  node_name: string | null;
  cultivar_area_id: number | null;
  served_area_ha: number | null;
  served_area_basis: ServedAreaBasis | null;
  derived_flow_lpm: number | null;
  current_flow_lpm: number | null;
  matches_current: boolean;
  reason: ValveDemandReason;
  warnings: { level: string; code: string; message: string }[];
}

export interface ValveDemandArea {
  cultivar_area_id: number;
  cultivar_area_name: string | null;
  system_type_key: string | null;
  system_type_name: string | null;
  derived: DerivedFlow | null;
  valve_count: number;
}

export interface ValveDemand {
  valves: ValveDemandRow[];
  areas: ValveDemandArea[];
}

export interface SensorChannelOption {
  id: number;
  device_id: string;
  channel_index: number;
  channel_kind: 'analog' | 'digital';
  type_key: string;
  channel_name: string | null;
  device_name: string | null;
  config: Record<string, unknown>;
  farm_id: number | null;
}

export type DivergenceLevel = 'ok' | 'warn' | 'alert' | 'stale' | 'unknown';

export interface SensorDivergence {
  node_id: number;
  node_name: string;
  predicted_kpa: number | null;
  measured_kpa: number;
  delta_kpa: number | null;
  delta_pct: number | null;
  level: DivergenceLevel;
  reading_age_sec: number;
}

export interface LiveAnalysis {
  farm_id: number;
  open_valve_node_ids: number[];
  solve: SolveResult;
  divergences: SensorDivergence[];
  summary: {
    open_valves: number;
    total_demand_lpm: number;
    pressure_sensors: number;
    alerts: number;
    warnings: number;
    stale: number;
    routers_offline: number;
  };
  generated_at: string;
}

export interface ProgramAnalysisStepMapping {
  router_mac: string;
  relay_index: number;
  node_id: number;
  node_name: string;
}

export interface ProgramAnalysisStep {
  step_id: string;
  step_order: number;
  step_duration_sec: number;
  open_valve_node_ids: number[];
  mapped: ProgramAnalysisStepMapping[];
  unmapped: { router_mac: string; relay_index: number }[];
  /** Step relays that drive a pump (a linked source node). Absent from older servers. */
  pumps?: ProgramAnalysisStepMapping[];
  result: SolveResult | null;
  summary: {
    valve_count: number;
    unmapped_count: number;
    max_velocity_ms: number;
    min_pressure_kpa: number | null;
    error_count: number;
    warn_count: number;
    status: 'ok' | 'warn' | 'error' | 'empty';
  };
}

export interface ProgramAnalysis {
  program: {
    id: string;
    name: string;
    controller_id: string;
    days_of_week: number[];
    start_time_sec: number;
    enabled: boolean;
  };
  controller: { controller_id: string; farm_id: number | null; controller_name: string | null };
  farm_id: number | null;
  steps: ProgramAnalysisStep[];
  meta: {
    skipped: boolean;
    skip_reason?: string;
    total_steps?: number;
    step_status_counts?: { ok: number; warn: number; error: number; empty: number };
  };
}

export interface IrrigationScenario {
  id: number;
  farm_id: number;
  organisation_id: number;
  name: string;
  notes: string | null;
  open_valve_node_ids: number[];
  created_at: string;
  updated_at: string;
  created_by: number | null;
}

export interface ScenarioInput {
  name: string;
  notes?: string | null;
  open_valve_node_ids?: number[];
}

export type SolveRequest =
  | { scenario_id: number; open_valve_node_ids?: never }
  | { open_valve_node_ids: number[]; scenario_id?: never };

export interface SolveNodeResult {
  id: number;
  pressure_kpa: number | null;
  head_m: number | null;
  demand_lpm: number;
}

export interface SolvePipeResult {
  id: number;
  flow_lpm: number;
  velocity_ms: number;
  headloss_m: number;
}

export interface SolveWarning {
  level: 'info' | 'warn' | 'error';
  code: string;
  message: string;
  node_id?: number;
  pipe_id?: number;
}

export interface SolveResult {
  nodes: SolveNodeResult[];
  pipes: SolvePipeResult[];
  warnings: SolveWarning[];
  meta: { sources: number; open_valves: number; total_demand_lpm: number };
}

export const irrigationNetworkService = new IrrigationNetworkService();
export default irrigationNetworkService;
